import { useFormikContext } from 'formik'
import isEqual from 'lodash/isEqual'
import pick from 'lodash/pick'
import React, { forwardRef, useState } from 'react'
import { deepDiff } from '~/common/objectUtils'
import { Button, Card, Flex, Stack } from '~/design_system'
import useDebouncedEffect from '~/hooks/useDebouncedEffect'
import usePrevious from '~/hooks/usePrevious'

/**
 * Usage Example
 *
 * // SomeCustomField.jsx
 * const SomeCustomField = (props) => <input {...props} />
 *
 * export default asFormikField(SomeCustomField, ({ field, form }) => ({
 *   onChange: val => form.setFieldValue(field.name, val)
 * }))
 *
 * // Elsewhere in a Formik Form
 * <Field name="myField" component={SomeCustomField} {...moreProps} />
 *
 * @param Component
 * @param propMapper {Function} A function that receives all the Field props and maps them to a props object
 */
export const asFormikField = (Component, propMapper = () => ({})) => {
  /**
   * @type {React.ForwardRefExoticComponent<React.PropsWithoutRef<React.ComponentProps<Component> & {readonly field?: *, readonly form?: *}> & React.RefAttributes<unknown>>}
   */
  const FormikFieldMapper = forwardRef(({ field = undefined, form = undefined, ...props }, ref) => (
    <Component ref={ref} {...field} {...(field && form ? propMapper({ field, form, ...props }) : {})} {...props} />
  ))
  FormikFieldMapper.displayName = `AsFormikField(${Component.displayName || Component.name})`
  FormikFieldMapper.InnerNonFormikCmp = Component
  return FormikFieldMapper
}

export function FormikValuesObserver({ onChange = () => {}, keys = [], debounceDelay = 500 }) {
  const { values } = useFormikContext()
  const previousValues = usePrevious(values)
  const getObservedValues = values => (keys ? pick(values, keys) : values)

  useDebouncedEffect(
    () => {
      if (!previousValues) {
        return
      }

      const observedValues = getObservedValues(values)
      const previousObservedValues = getObservedValues(previousValues)
      if (!isEqual(previousObservedValues, observedValues)) {
        onChange(observedValues)
      }
    },
    { delay: debounceDelay, deepEquals: true },
    [values]
  )
  return null
}

export function FormikDevtool({ initialData }) {
  const { values, errors, ...info } = useFormikContext()
  const [showDiff, setShowDiff] = useState(false)
  const [showValues, setShowValues] = useState(false)
  const [showErrors, setShowErrors] = useState(false)
  const [showInfo, setShowInfo] = useState(false)

  return (
    <>
      <Flex>
        <Button size="sm" onClick={() => setShowValues(prev => !prev)}>
          Values
        </Button>
        <Button size="sm" onClick={() => setShowDiff(prev => !prev)}>
          Diff
        </Button>
        <Button size="sm" onClick={() => setShowErrors(prev => !prev)}>
          Errors
        </Button>
        <Button size="sm" onClick={() => setShowInfo(prev => !prev)}>
          Info
        </Button>
      </Flex>
      <Stack>
        {showDiff && (
          <Card>
            <pre>diff: {JSON.stringify(deepDiff(initialData, values), null, 2)}</pre>
          </Card>
        )}
        {showValues && (
          <Card>
            <pre>{JSON.stringify(values, null, 2)}</pre>
          </Card>
        )}
        {showErrors && (
          <Card>
            <pre>{JSON.stringify(errors, null, 2)}</pre>
          </Card>
        )}
        {showInfo && (
          <Card>
            <pre>{JSON.stringify(info, null, 2)}</pre>
          </Card>
        )}
      </Stack>
    </>
  )
}
